import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  HandCoins,
  Brain,
  ChevronLeft,
  ChevronRight,
  Menu,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "../contexts/AuthContext";

const navigationItems = [
  { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, description: "Overview and analytics" },
  { title: "Deals", href: "/deals", icon: HandCoins, description: "Manage all deals" },
  { title: "AI Insights", href: "/ai-insights", icon: Brain, description: "AI-powered analysis" },
];

const SIDEBAR_SOURCE_KEY = "breadcrumb:sidebarSource";

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    try { m.addEventListener("change", onChange); } catch { m.addListener(onChange as any); }
    return () => {
      try { m.removeEventListener("change", onChange); } catch { m.removeListener(onChange as any); }
    };
  }, [query]);
  return matches;
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const location = useLocation();
  const { logout } = useAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const mobileOpen = isMobile && !collapsed;

  useEffect(() => {
    if (!isMobile) return;
    const html = document.documentElement;
    if (mobileOpen) {
      const prev = html.style.overflow;
      html.style.overflow = "hidden";
      return () => { html.style.overflow = prev; };
    }
  }, [mobileOpen, isMobile]);

  const rememberSource = (label: string, path: string) => {
    try {
      sessionStorage.setItem(SIDEBAR_SOURCE_KEY, JSON.stringify({ label, path }));
    } catch {}
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      <div
        className={cn(
          "flex flex-col h-screen bg-sidebar-background border-r border-sidebar-border transition-all duration-300 bg-background",
          isMobile
            ? cn(
                "fixed left-0 top-0 z-[60] w-[280px] shadow-2xl",
                mobileOpen ? "translate-x-0" : "-translate-x-full"
              )
            : collapsed
            ? "w-16"
            : "w-64"
        )}
        style={isMobile ? { paddingTop: "max(env(safe-area-inset-top), 0px)" } : undefined}
      >
        <div className={cn("flex items-center border-b border-sidebar-border h-[72px]", collapsed && !isMobile ? "justify-around px-2" : "justify-between px-4")}>
          {collapsed && !isMobile ? (
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center transition-all duration-300 ease-out">
                <Brain className="w-5 h-5 text-primary-foreground" />
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 overflow-hidden">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center transition-all duration-300 ease-out flex-shrink-0">
                <Brain className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className={cn("flex flex-col transition-all duration-300 ease-out", collapsed && !isMobile ? "w-0 opacity-0" : "w-auto opacity-100")}>
                <h2 className="text-sm font-semibold text-sidebar-foreground whitespace-nowrap">Sidebar Lens</h2>
                <p className="text-xs text-sidebar-foreground/60 whitespace-nowrap">AI Assistant</p>
              </div>
            </div>
          )}
          
          {/* Toggle button - always visible on desktop */}
          {!isMobile && (
            <div
              role="button"
              aria-label="Toggle sidebar"
              tabIndex={0}
              onClick={onToggle}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
              className="cursor-pointer text-sidebar-foreground/60 hover:text-sidebar-foreground p-1 rounded-md"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </div>
          )}
          
          {/* Mobile close button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-8 w-8 p-0 hover:bg-sidebar-accent transition-colors duration-300 ease-out"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            const baseClasses = cn(
              "flex items-center space-x-3 px-2 py-2 rounded-lg text-sm font-light transition-all duration-300 ease-out",
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-gray-600"
            );

            const collapsedClasses = cn(
              "flex items-center space-x-3 px-2 py-2 justify-center rounded-lg text-sm font-light transition-all duration-300 ease-out",
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-gray-600"
            );

            return !collapsed || isMobile ? (
              <NavLink
                key={item.href}
                to={item.href}
                state={{ sidebarSource: { label: item.title, path: item.href } }}
                onClick={() => {
                  rememberSource(item.title, item.href);
                  if (isMobile && mobileOpen) onToggle();
                }}
                className={baseClasses}
              >
                <Icon className="w-5 h-5 flex-shrink-0 transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
                {(isMobile || !collapsed) && (
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{item.title}</div>
                    <div className="text-xs opacity-70 truncate">{item.description}</div>
                  </div>
                )}
              </NavLink>
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                state={{ sidebarSource: { label: item.title, path: item.href } }}
                onClick={() => rememberSource(item.title, item.href)}
                className={collapsedClasses}
                title={collapsed ? item.title : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0 transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
              </NavLink>
            );
          })}
        </nav>
      </div>


    </>
  );
}
